// ── lib/intakeStoreErrors.js ──
// Error class + classifiers for the async intakeStore.
//
// Contract (ARCHITECTURE.md §G): RLS denial, network failure, schema
// violation — all surface as IntakeStoreError. The `friendlyMessage`
// field is text safe to render in the UI without leaking Postgres
// internals to a workshop guest.

export class IntakeStoreError extends Error {
  constructor(message, { code, cause, friendlyMessage } = {}) {
    super(message);
    this.name = "IntakeStoreError";
    this.code = code || null;
    this.cause = cause || null;
    this.friendlyMessage = friendlyMessage || message;
  }
}

// Postgres SQLSTATE codes we recognize.
//   42501 = insufficient_privilege (RLS denial)
//   23505 = unique_violation
//   23503 = foreign_key_violation
//   23502 = not_null_violation
//   PGRST = PostgREST family (Supabase REST translation layer)

export function isPermissionError(err) {
  return err?.code === "42501" || /permission denied|RLS/i.test(err?.message || "");
}

export function isUniqueViolation(err) {
  return err?.code === "23505";
}

export function isForeignKeyViolation(err) {
  return err?.code === "23503";
}

export function isNotNullViolation(err) {
  return err?.code === "23502";
}

export function isNetworkError(err) {
  // supabase-js wraps fetch failures with `message` like 'Failed to fetch'
  // or `name: 'NetworkError'`. The lib does not expose a stable code here.
  if (!err) return false;
  if (err.name === "NetworkError") return true;
  return /failed to fetch|network|timed out/i.test(err.message || "");
}

// Supabase Auth error codes (AuthError.code) we special-case so the user
// sees an actionable message instead of the generic fallback "Something
// went wrong." Codes follow the supabase-js v2 AuthApiError taxonomy.
const AUTH_FRIENDLY_MESSAGES = {
  invalid_credentials: "Invalid email or password.",
  email_not_confirmed: "Email not confirmed. Check your inbox for the confirmation link.",
  over_request_rate_limit: "Too many attempts. Please wait a minute and try again.",
  user_not_found: "Invalid email or password.",
  weak_password: "Password is too weak. Pick a stronger one.",
  user_already_exists: "An account with that email already exists.",
  email_address_invalid: "That doesn't look like a valid email address.",
  signup_disabled: "Sign-ups are disabled. Contact your administrator.",
  validation_failed: "Some required information is missing or invalid.",
};

function authFriendlyMessage(err) {
  const code = err?.code || err?.error_code;
  return code && AUTH_FRIENDLY_MESSAGES[code] ? AUTH_FRIENDLY_MESSAGES[code] : null;
}

// Single entry point for every Supabase call site:
//   const { data, error } = await supabase.from(...).select(...);
//   if (error) throw classify(error, 'list events');
export function classify(err, action = "operation") {
  if (!err) return new IntakeStoreError(`Unknown ${action} error`);

  // Supabase Auth errors come back with err.code (e.g. invalid_credentials).
  // Match those before the SQLSTATE branches so a sign-in failure does not
  // fall through to the generic "Something went wrong" message.
  const authMsg = authFriendlyMessage(err);
  if (authMsg) {
    return new IntakeStoreError(`${action}: ${err.code || err.message}`, {
      code: err.code || null,
      cause: err,
      friendlyMessage: authMsg,
    });
  }

  if (isPermissionError(err)) {
    return new IntakeStoreError(`RLS denied during ${action}`, {
      code: err.code,
      cause: err,
      friendlyMessage: "Your session expired — please re-enter the access code.",
    });
  }

  if (isUniqueViolation(err)) {
    return new IntakeStoreError(`Uniqueness conflict during ${action}`, {
      code: err.code,
      cause: err,
      friendlyMessage: "That record already exists. Please refresh and try again.",
    });
  }

  if (isForeignKeyViolation(err)) {
    return new IntakeStoreError(`Foreign-key violation during ${action}`, {
      code: err.code,
      cause: err,
      friendlyMessage: "Linked record is missing or has been deleted. Please refresh.",
    });
  }

  if (isNotNullViolation(err)) {
    return new IntakeStoreError(`Required field missing during ${action}`, {
      code: err.code,
      cause: err,
      friendlyMessage: "A required field is missing. Please review the form and resubmit.",
    });
  }

  if (isNetworkError(err)) {
    return new IntakeStoreError(`Network failure during ${action}`, {
      code: "network",
      cause: err,
      friendlyMessage: "Network error — your changes were not saved. Please try again.",
    });
  }

  return new IntakeStoreError(`Unexpected error during ${action}: ${err.message || err}`, {
    code: err.code || null,
    cause: err,
    friendlyMessage: "Something went wrong. Please refresh the page and try again.",
  });
}
